import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import fileUploadUtils from '../../utils/fileUploadUtils';

// Custom styles for small previews
const smallPreviewStyle = {
  maxHeight: '80px',
  maxWidth: '80px',
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden'
};

const filePreviewItemStyle = {
  border: '1px solid #ddd',
  borderRadius: '4px',
  padding: '4px',
  marginBottom: '8px',
  position: 'relative',
  maxWidth: '80px'
};

const FileAttachment = ({ 
  onChange, 
  maxFiles = 1, 
  maxSize = fileUploadUtils.MAX_FILE_SIZE,
  showPreview = true,
  previewSize = 'small',
  disabled = false,
  label = 'Attach File',
  helpText = ''
}) => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [errors, setErrors] = useState([]);
  
  // Notify parent component when files change
  useEffect(() => {
    if (onChange) {
      onChange(files);
    }
  }, [files, onChange]);
  
  const handleFileChange = async (e) => {
    const selected = e.target.files;
    const validation = await fileUploadUtils.validateFilesAsync(selected, {
      maxFiles,
      maxSize,
    });

    // Allow re-selecting the same file after a rejected attempt
    e.target.value = "";

    if (validation.hasErrors) {
      setErrors(validation.errors);
      return;
    }

    // Clear previous errors
    setErrors([]);

    // Check if adding these files would exceed maxFiles
    if (files.length + validation.validFiles.length > maxFiles) {
      setErrors([`Maximum ${maxFiles} files allowed.`]);
      return;
    }

    // Add new files to existing files
    const updatedFiles = [...files, ...validation.validFiles];
    setFiles(updatedFiles);

    // Create previews for new files
    const newPreviews = validation.validFiles.map(
      fileUploadUtils.createFilePreview
    );
    setPreviews([...previews, ...newPreviews]);
  };
  
  const removeFile = (index) => {
    // Create new arrays without the file at specified index
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    
    const updatedPreviews = [...previews];
    if (updatedPreviews[index] && updatedPreviews[index].preview) {
      URL.revokeObjectURL(updatedPreviews[index].preview);
    }
    updatedPreviews.splice(index, 1);
    
    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
  };
  
  const clearFiles = () => {
    // Revoke all preview URLs to prevent memory leaks
    previews.forEach(preview => {
      if (preview.preview) {
        URL.revokeObjectURL(preview.preview);
      }
    });
    
    setFiles([]);
    setPreviews([]);
    setErrors([]);
  };
  
  // Clean up preview URLs when component unmounts
  useEffect(() => {
    return () => {
      previews.forEach(preview => {
        if (preview.preview) {
          URL.revokeObjectURL(preview.preview);
        }
      });
    };
  }, [previews]);
  
  return (
    <div className="file-attachment">
      {/* Label */}
      {label && <label className="form-label">{label}</label>}
      
      {/* File Input */}
      <div className="input-group mb-3">
        <input
          type="file"
          className="form-control"
          // accept="pdf"
          id="fileAttachment"
          onChange={handleFileChange}
          disabled={disabled || files.length >= maxFiles}
          multiple={maxFiles > 1}
          accept={Object.keys(fileUploadUtils.ALLOWED_FILE_TYPES).map(ext => `.${ext}`).join(',')}
        />
        {files.length > 0 && (
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={clearFiles}
            disabled={disabled}
          >
            Clear All
          </button>
        )}
      </div>
      
      {/* Help Text */}
      {helpText && <div className="form-text mb-3">{helpText}</div>}
      
      {/* Error Display */}
      {errors.length > 0 && (
        <div className="alert alert-danger mb-3">
          <ul className="mb-0">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* File Preview - Small Size */}
      {showPreview && previews.length > 0 && (
        <div className="mb-3">
          <div className="row g-2">
            {previews.map((preview, index) => (
              <div key={index} className="col-3 col-md-2" style={{ maxWidth: '80px' }}>
                <div style={filePreviewItemStyle}>
                  {preview.isImage ? (
                    <div style={smallPreviewStyle}>
                      <img 
                        src={preview.preview} 
                        alt={preview.name} 
                        style={{ maxHeight: '100%', maxWidth: '100%' }}
                      />
                    </div>
                  ) : (
                    <div style={smallPreviewStyle}>
                      <i className={preview.icon} style={{ fontSize: '18px' }}></i>
                    </div>
                  )}
                  <div style={{ padding: '2px 0', textAlign: 'center' }}>
                    <p className="text-truncate mb-0" title={preview.name} style={{ fontSize: '9px' }}>
                      {preview.name}
                    </p>
                    <p className="text-muted mb-0" style={{ fontSize: '8px' }}>
                      {fileUploadUtils.formatFileSize(preview.size)}
                    </p>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close position-absolute top-0 end-0" 
                    style={{ fontSize: '0.5rem', padding: '2px' }}
                    onClick={() => removeFile(index)}
                    disabled={disabled}
                    aria-label="Remove file"
                  ></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

FileAttachment.propTypes = {
  onChange: PropTypes.func,
  maxFiles: PropTypes.number,
  maxSize: PropTypes.number,
  showPreview: PropTypes.bool,
  previewSize: PropTypes.oneOf(['small', 'medium', 'large']),
  disabled: PropTypes.bool,
  label: PropTypes.string,
  helpText: PropTypes.string
};

export default FileAttachment;